import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { KpiCardDefinition } from '@myarvato/components/kpi';

@Injectable({
  providedIn: 'root',
})
export class KpiCardProviderService {
  // injections
  private readonly translateService = inject(TranslateService);

  public GetKpiCardDefinitions(): KpiCardDefinition[] {
    return [
      // default cards
      KpiCardProviderService.createKpiCardDefinition({
        field: 'Total',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.total.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.total.tooltip'),
        icon: 'pi pi-box',
        iconColor: 'text-info',
        filters: {},
        isDefault: true,
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'Delayed',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.delayed.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.delayed.tooltip'),
        icon: 'pi pi-clock',
        iconColor: 'text-info',
        filters: { DaysDelayed: [{ value: '0', matchMode: 'gt', operator: 'or' }] },
        isDefault: true,
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'Exception',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.exception.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.exception.tooltip'),
        icon: 'pi pi-exclamation-circle',
        iconColor: 'text-danger',
        filters: {
          Severity: [{ value: ['EXCEPTION'], matchMode: 'in', operator: 'or' }],
        },
        isDefault: true,
      }),

      // additional cards
      KpiCardProviderService.createKpiCardDefinition({
        field: 'ReadyForShipping',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.ready-for-shipping.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.ready-for-shipping.tooltip'),
        icon: 'pi pi-box',
        iconColor: 'text-info',
        filters: { Progress: [{ value: ['READY'], matchMode: 'in', operator: 'or' }] },
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'InTransit',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.in-transit.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.in-transit.tooltip'),
        icon: 'pi pi-truck',
        iconColor: 'text-info',
        filters: { Progress: [{ value: ['INTRANSIT'], matchMode: 'in', operator: 'or' }] },
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'WaitingForPickup',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.waiting.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.waiting.tooltip'),
        icon: 'pi pi-hourglass',
        iconColor: 'text-info',
        filters: { Progress: [{ value: ['WAITING'], matchMode: 'in', operator: 'or' }] },
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'Delivered',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.delivered.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.delivered.tooltip'),
        icon: 'pi pi-home',
        iconColor: 'text-info',
        filters: { Progress: [{ value: ['DELIVERED'], matchMode: 'in', operator: 'or' }] },
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'Returned',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.returned.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.returned.tooltip'),
        icon: 'pi pi-warehouse',
        iconColor: 'text-info',
        filters: { Progress: [{ value: ['RETURN'], matchMode: 'in', operator: 'or' }] },
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'ReturningToSender',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.returning-to-sender.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.returning-to-sender.tooltip'),
        icon: 'pi pi-arrow-left',
        iconColor: 'text-info',
        filters: { Progress: [{ value: ['RET_SENDER'], matchMode: 'in', operator: 'or' }] },
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'NeedAttention',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.needs-attention.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.needs-attention.tooltip'),
        icon: 'pi pi-clock',
        iconColor: 'text-info',
        filters: { ResolutionStatus: [{ value: ['NEED_ATTENTION'], matchMode: 'in', operator: 'or' }] },
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'InProgress',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.in-progress.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.in-progress.tooltip'),
        icon: 'pi pi-spinner',
        iconColor: 'text-info',
        filters: {
          ResolutionStatus: [
            {
              value: ['UNDER_INVESTIGATION', 'WAIT_ARVATO', 'WAIT_CARRIER', 'WAIT_CUSTOMER', 'WAIT_CLIENT'],
              matchMode: 'in',
              operator: 'or',
            },
          ],
        },
      }),
      KpiCardProviderService.createKpiCardDefinition({
        field: 'Solved',
        title: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.solved.title'),
        tooltip: this.translateService.instant('pages.transport.tracking-list.kpi-bar.cards.solved.tooltip'),
        icon: 'pi pi-check-circle',
        iconColor: 'text-info',
        filters: { ResolutionStatus: [{ value: ['SOLVED'], matchMode: 'in', operator: 'or' }] },
      }),
    ];
  }

  private static createKpiCardDefinition(definition: KpiCardDefinition): KpiCardDefinition {
    return definition;
  }
}
